import React from "react";
import * as Sentry from "@sentry/react";

/**
 * BLOCK S12 Item 4a — the ONE boundary above everything.
 *
 * It is mounted in index.js OUTSIDE <App/>, so it sits above the router, the
 * auth/root component and every context provider. A crash anywhere below —
 * including the root auth-restore effect that used to blank the whole page —
 * lands here instead of an empty screen.
 *
 * The fallback is deliberately self-contained: no storage, no API, no context/
 * provider (it renders above all of them), inline styles only, so it can render
 * even when the thing that crashed is a provider it sits above.
 */
class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    try {
      Sentry.withScope((scope) => {
        scope.setTag("blank_screen", "crash");
        try {
          scope.setTag("route", window.location.pathname);
        } catch (_e) {
          /* location unavailable — still report */
        }
        if (info && info.componentStack) {
          scope.setExtra("componentStack", info.componentStack);
        }
        Sentry.captureException(error);
      });
    } catch (_e) {
      /* reporting must never re-throw out of the boundary */
    }
  }

  handleReload() {
    try {
      window.location.reload();
    } catch (_e) {
      /* noop */
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#0B0F1A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        }}
      >
        <div style={{ width: "100%", maxWidth: "390px", textAlign: "center" }}>
          <h1
            style={{
              color: "#F3F5FA",
              fontSize: "22px",
              fontWeight: 700,
              margin: "0 0 12px 0",
            }}
          >
            Something went wrong
          </h1>
          <p
            style={{
              color: "#8A93A6",
              fontSize: "15px",
              lineHeight: 1.6,
              margin: "0 0 28px 0",
            }}
          >
            Please reload — if it keeps happening, contact the business that sent
            you this link.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "44px",
              padding: "0 28px",
              background: "#D4AF37",
              color: "#0B0F1A",
              fontSize: "15px",
              fontWeight: 700,
              border: "none",
              borderRadius: "12px",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default RootErrorBoundary;
